<template>
  <sc-render :fields="fields" :emptyMessage="$t('NO_DATA_SOURCE_EXIST')">
    <component :is="cardType" v-bind="{ ...propTypes, ...tileClasses }">
      <ImageCard :isLarge="isLargeImage">
        <template #image>
          <div v-if="isVideo" class="video-container">
            <div v-if="isEditMode" class="video-placeholder" />
            <vimeo-video
              v-else
              :url="videoUrl"
              :autoplay="true"
              :mute="true"
              :loop="loop"
              :defaultControls="false"
              :hidden-text="hiddenVideoTitle"
              @error="isVideoFailed = true"
            >
              <ShImage :src="setImageSrc" :alt="imageAlt" />
            </vimeo-video>
          </div>
          <div v-else class="image-container">
            <sc-image v-if="isEditMode" :media="imageField" />
            <ShImage v-else class="img" :src="setImageSrc" :alt="imageAlt" />
          </div>
        </template>
        <template #title>
          <heading v-if="hasTitle"
            class="title"
            desktop-size="size-5"
            mobile-size="size-5"
            :textField="fields.title"
            :tagField="headingSize"
            default-tag="h3"
          />
        </template>
        <template #description>
          <sc-text v-if="hasDescription" class="description" :field="fields.description" />
        </template>
        <template #tag>
          <sc-text v-if="hasTag" class="tag" :field="fields.tag" />
        </template>
        <template #date>
          <sc-text v-if="hasDate" class="data" :field="fields.date" />
        </template>
        <template #location>
          <sc-text v-if="hasLocation" class="location" :field="fields.location" />
        </template>
        <template #default>
          <sc-link v-if="isEditMode" class="sc-link" :field="fields.link" />
          <sh-button
            v-if="linkHref && !isTile && !isEditMode"
            class="link"
            :naked="isNaked"
            :role="!isVideo && 'button'"
            v-bind="hasLinkAttrs"
          >
            {{ fields.link.value.text }}
          </sh-button>
        </template>
      </ImageCard>
    </component>
  </sc-render>
</template>

<script>
import { Image, Text, Link } from '@sitecore-jss/sitecore-jss-vue'
import { ImageCard, ShImage } from '@sohohouse/design-system'
import { createNamespacedHelpers } from 'vuex'

import { getButtonType } from '~/helpers/get-button-type'
import ShLink from '~/components/sh-link'
import get from '~/helpers/get'
import { decreaseTagValue } from '~/helpers/decrease-tag-value'
import matchRatio from '~/helpers/match-ratio'

import VimeoVideo from '../vimeo-video'
import ScRender from '../sc-render'
import Heading from '../heading'

const { mapGetters: mapJssGetters } = createNamespacedHelpers('jss')
const { mapGetters: mapEnvGetters } = createNamespacedHelpers('ui/environment')

export default {
  name: 'CarouselTile',

  props: {
    fields: {
      type: Object,
      default: () => ({})
    }
  },

  data () {
    return {
      isVideoFailed: false
    }
  },

  components: {
    ScImage: Image,
    ScLink: Link,
    ScText: Text,
    ShLink,
    ScRender,
    ShImage,
    Heading,
    ImageCard,
    VimeoVideo
  },

  computed: {
    ...mapJssGetters(['isEditMode']),
    ...mapEnvGetters(['isDarkTheme']),

    videoUrl () {
      return get(this.fields, 'mainVideo.value', '')
    },

    loop () {
      return get(this.fields, 'loop.value', false)
    },

    hiddenVideoTitle () {
      return get(this.fields, 'hiddenVideoTitle.value', '')
    },

    headingSize () {
      if (this.isEditMode) {
        return decreaseTagValue(get(this.$parent, 'rendering.fields.headingSize'))
      }
      return decreaseTagValue(get(this.fields, 'headingSize'))
    },

    imageField () {
      return get(this.fields, 'image')
    },

    imageAlt () {
      return get(this.imageField, 'value.alt')
    },

    imageHeight () {
      return get(this.imageField, 'value.height')
    },

    imageWidth () {
      return get(this.imageField, 'value.width')
    },

    linkHref () {
      return get(this.fields, 'link.value.href', '')
    },

    linkType () {
      return get(this.fields, 'link.value.linktype')
    },

    linkTarget () {
      return get(this.fields, 'link.value.target', '')
    },

    hasLinkAttrs () {
      const linkType = getButtonType(this.linkHref)

      return this.isVideo ? { [linkType]: this.linkHref, target: this.linkTarget } : {}
    },

    hasDescription () {
      return this.isEditMode || get(this.fields, 'description.value', false)
    },

    hasTitle () {
      return this.isEditMode || get(this.fields, 'title.value', false)
    },

    hasTag () {
      return this.isEditMode || get(this.fields, 'tag.value', false)
    },

    hasDate () {
      return this.isEditMode || get(this.fields, 'date.value', false)
    },

    hasLocation () {
      return this.isEditMode || get(this.fields, 'location.value', false)
    },

    isNaked () {
      return this.fields.ctaAppearance && this.fields.ctaAppearance.value !== 'Button'
    },

    isTile () {
      return this.fields.ctaAppearance && this.fields.ctaAppearance.value === 'Tile'
    },

    isVideo () {
      return this.videoUrl && !this.isVideoFailed
    },

    setImageSrc () {
      return this.imageField.value.src
    },

    cardType () {
      if (!this.linkHref || this.isVideo || this.isEditMode) return 'div'

      return 'sh-link'
    },

    propTypes () {
      if (this.cardType === 'div') {
        return { tabindex: '0' }
      }

      return {
        to: this.linkHref,
        target: this.linkTarget
      }
    },

    tileClasses () {
      return {
        class: [
          'tile',
          {
            'tile-link': this.linkHref && !this.isVideo,
            'tile-editor': this.isEditMode,
            'tile-only': this.isTile,
            'tile-link-black': this.isDarkTheme
          }
        ]
      }
    },

    isLargeImage () {
      return matchRatio({
        ratioWidth: 3,
        ratioHeight: 4,
        width: this.imageWidth,
        height: this.imageHeight,
        measurementError: 2 })
    }
  }
}
</script>

<style lang="scss" scoped>

.video-container {
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  width: 100%;
}

.tile-editor {
  margin-right: $space-050;

  .video-placeholder {
    width: 100%;
    height: 100%;
    @include placeholder-image('~assets/images/video-placeholder.png');
  }
}

a {
  text-decoration: none;
}

.tile-link-black {
  @include outline-focus ($color-white);
}

</style>
